/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import axios from 'axios';
import * as auth from '../redux/AuthRedux';
import { login } from '../redux/AuthCRUD';
import { toast } from 'react-toastify';
import { SimpleAlert } from '../../../../rbts/helpers';

const API_URL = process.env.REACT_APP_API;

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Formato de correo inválido.')
    .min(3, 'Mínimo 3 caracteres.')
    .max(50, 'Máximo 50 caracteres.')
    .required('Correo es requerido.'),
  password: Yup.string()
    .matches(
      /^(?=.*[A-Z])(?=.*[@#$%^&-+=()])(?=.*[0-9])(?=.*[a-z])(?=\S+$).{8,20}$/,
      'Contraseña incompleta'
    )
    .required('Debes teclear la Contraseña'),
});

const initialValues = {
  email: '',
  password: '',
};

type EventoActivo = { id: number; nombre: string; fecha_inicio: string; fecha_fin: string; imagen: string | null };

export function Login() {
  const [loading, setLoading] = useState(false);
  const [visivility, setVisivility] = useState(false);
  // Paso 2: selector de evento
  const [step, setStep] = useState<'credentials' | 'evento'>('credentials');
  const [tempToken, setTempToken] = useState<string>('');
  const [eventosActivos, setEventosActivos] = useState<EventoActivo[]>([]);
  const [eventoSeleccionado, setEventoSeleccionado] = useState<number | null>(null);
  const [loadingEvento, setLoadingEvento] = useState(false);

  const validateVisibility = () => setVisivility(!visivility);

  const dispatch = useDispatch();

  const handleSelectEvento = async () => {
    if (!eventoSeleccionado) {
      SimpleAlert('Selecciona un evento para continuar.', 3000, 'warning');
      return;
    }
    setLoadingEvento(true);
    try {
      const selectResp = await axios.post(
        `${API_URL}select-evento`,
        { evento_id: eventoSeleccionado },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      dispatch(auth.actions.login(selectResp.data.accessToken));
      toast('Bienvenido!', { position: 'top-center', autoClose: 3000, theme: 'light' });
    } catch {
      SimpleAlert('No se pudo seleccionar el evento. Intenta de nuevo.', 3000, 'error');
    } finally {
      setLoadingEvento(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setTimeout(() => {
        login(values.email, values.password)
          .then(async (res) => {
            const { accessToken, user } = res.data;

            // Bloquear roles exclusivos de la app móvil
            const restrictedRoles = ['CAJA', 'PUESTO'];
            const hasRestrictedRole = user?.roles?.some((role: any) => {
              const code = role?.code ?? role?.dataValues?.code;
              return restrictedRoles.includes(code);
            });

            if (hasRestrictedRole) {
              setLoading(false);
              setSubmitting(false);
              SimpleAlert(
                'Acceso Restringido: Su perfil (Caja/Puesto) es de uso exclusivo en la Aplicación Móvil.',
                5000,
                'warning'
              );
              return;
            }

            // Guardar token temporal para el paso 2
            setTempToken(accessToken);
            const authHeader = { Authorization: `Bearer ${accessToken}` };

            try {
              // Llamadas con el token explícito en el header (el Redux store aún no tiene el token)
              const eventosResp = await axios.get(`${API_URL}eventos-activos`, { headers: authHeader });
              const eventos: EventoActivo[] = eventosResp.data?.data ?? [];

              if (eventos.length === 1) {
                // Un solo evento: seleccionar automáticamente
                const selectResp = await axios.post(`${API_URL}select-evento`, { evento_id: eventos[0].id }, { headers: authHeader });
                dispatch(auth.actions.login(selectResp.data.accessToken));
                toast('Bienvenido!', { position: 'top-center', autoClose: 3000, theme: 'light' });
              } else if (eventos.length > 1) {
                setEventosActivos(eventos);
                setEventoSeleccionado(eventos[0].id);
                setStep('evento');
              } else {
                // Sin eventos activos: usar token sin evento_id
                dispatch(auth.actions.login(accessToken));
                toast('Bienvenido!', { position: 'top-center', autoClose: 3000, theme: 'light' });
              }
            } catch {
              // Si falla la carga de eventos, continuar sin evento_id
              dispatch(auth.actions.login(accessToken));
              toast('Bienvenido!', { position: 'top-center', autoClose: 3000, theme: 'light' });
            }

            setLoading(false);
          })
          .catch((err) => {
            let resMessageToast: string = '';
            if (err.response && err.response.data) {
              const { data: { message, details = null } } = err.response;
              resMessageToast = `${message}`;
              if (details && details.length) setStatus(`${details[0]?.message}`);
              SimpleAlert(resMessageToast, 3000, 'error');
            } else {
              SimpleAlert('Error al iniciar sesión', 3000, 'error');
            }
            setLoading(false);
            setSubmitting(false);
          });
      }, 1000);
    },
  });

  if (step === 'evento') {
    return (
      <div className="form w-100">
        <div className="text-center mb-10">
          <h1 className="text-dark mb-3">Seleccionar Evento</h1>
          <p className="text-gray-400 fw-bold fs-4">
            Selecciona el evento con el que trabajarás en esta sesión.
          </p>
        </div>

        <div className="fv-row mb-10">
          <label className="form-label fs-6 fw-bolder text-dark">Evento</label>
          <select
            className="form-select form-select-lg"
            value={eventoSeleccionado ?? ''}
            onChange={(e) => setEventoSeleccionado(Number(e.target.value))}
          >
            {eventosActivos.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="text-center">
          <button
            type="button"
            className="btn btn-lg btn-primary w-100 mb-5"
            onClick={handleSelectEvento}
            disabled={loadingEvento || !eventoSeleccionado}
          >
            {!loadingEvento && <span className="indicator-label">Ingresar</span>}
            {loadingEvento && (
              <span className="indicator-progress" style={{ display: 'block' }}>
                Espere por favor...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="form w-100"
      onSubmit={formik.handleSubmit}
      noValidate
      id="kt_login_signin_form"
    >
      {/* begin::Heading */}
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">Iniciar Sesión</h1>
      </div>
      {/* begin::Heading */}

      {formik.status && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formik.status}</div>
        </div>
      )}
      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">Correo</label>
        <input
          placeholder="Correo"
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control form-control-lg'
            // {'is-invalid': formik.touched.email && formik.errors.email},
            // {
            //   'is-valid': formik.touched.email && !formik.errors.email,
            // }
          )}
          type="email"
          name="email"
          autoComplete="off"
        />
        {formik.touched.email && formik.errors.email && (
          <div className="fv-plugins-message-container text-danger mt-3">
            <span role="alert">{formik.errors.email}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <div className="d-flex justify-content-between mt-n5">
          <div className="d-flex flex-stack mb-2">
            {/* begin::Label */}
            <label className="form-label fw-bolder text-dark fs-6 mb-0">
              Contraseña
            </label>
            {/* end::Label */}
          </div>
        </div>

        <div className={clsx('position-relative')}>
          <input
            type={visivility ? 'text' : 'password'}
            placeholder="Contraseña"
            autoComplete="off"
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg'
              // {
              //   'is-invalid': formik.touched.password && formik.errors.password,
              // },
              // {
              //   'is-valid': formik.touched.password && !formik.errors.password,
              // }
            )}
          />
          <span
            className={clsx(
              'btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2'
            )}
            data-kt-password-meter-control={'visibility'}
            onClick={validateVisibility}
          >
            {visivility ? (
              <i className={clsx('bi bi-eye fs-2')}></i>
            ) : (
              <i className={clsx('bi bi-eye-slash fs-2')}></i>
            )}
          </span>
        </div>

        {formik.touched.password && formik.errors.password && (
          <div className="fv-plugins-message-container text-danger mt-3">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_in_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className="indicator-label">Continuar</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Espere por favor...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
        <Link
          to="/auth/forgot-password"
          className="link-primary fs-6 fw-bolder"
        >
          Olvidó su contraseña ?
        </Link>
      </div>
      {/* end::Action */}
    </form>
  );
}
